import { useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ReportChart } from '../../../api/charts';
import { Donut3D, sliceColour, type DonutSlice } from './Donut3D';

function formatters(lang: string) {
  const locale = lang === 'en' ? 'en-GB' : 'sk-SK';
  const number = new Intl.NumberFormat(locale, { maximumFractionDigits: 2 });
  const percent = new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 1 });
  return { number: (v: string) => number.format(Number(v)), percent: (share: number) => percent.format(share) };
}

/**
 * One chart: the tilted 3D donut and its data table (the exact values of every item, including those folded into
 * "Ostatné" and those that cannot be charted). The table is the accessible equivalent of the image; hovering a row
 * or a slice emphasises the other. All texts are in the chart language (`lang`), independent of the UI.
 */
export function ReportChartView({ chart, lang, headingLevel = 3 }: { chart: ReportChart; lang: string; headingLevel?: 3 | 4 }) {
  const { t } = useTranslation();
  const headingId = useId();
  const [active, setActive] = useState<number | null>(null);
  const fmt = useMemo(() => formatters(lang), [lang]);
  const total = Number(chart.total);
  const tt = (key: string, options: Record<string, unknown> = {}) => t(key, { ...options, lng: lang });

  const slices: DonutSlice[] = chart.slices.map((s) => ({
    label: s.label.value,
    lang: s.label.lang ?? undefined,
    value: Number(s.value),
    valueText: fmt.number(s.value),
    shareText: total > 0 ? fmt.percent(Number(s.value) / total) : '–',
    other: s.other,
  }));
  const description = tt('charts.description', {
    items: slices.map((s) => `${s.label} ${s.valueText} (${s.shareText})`).join(', '),
    total: fmt.number(chart.total),
  });
  const Heading = headingLevel === 3 ? 'h3' : 'h4';

  return (
    <section className="app-chart" aria-labelledby={headingId}>
      <Heading className="govuk-heading-s" id={headingId} lang={chart.title.lang ?? undefined}>
        {chart.title.value}
      </Heading>
      <div className="app-chart__body">
        <Donut3D
          slices={slices}
          title={chart.title.value}
          description={description}
          totalText={tt('charts.totalShort', { total: fmt.number(chart.total) })}
          active={active}
          onActiveChange={setActive}
        />
        <div className="app-chart__table">
          <table className="govuk-table">
            <caption className="govuk-table__caption govuk-visually-hidden">{tt('charts.tableCaption', { title: chart.title.value })}</caption>
            <thead className="govuk-table__head">
              <tr className="govuk-table__row">
                <th scope="col" className="govuk-table__header">
                  {tt('charts.item')}
                </th>
                <th scope="col" className="govuk-table__header govuk-table__header--numeric">
                  {tt('charts.value')}
                </th>
                <th scope="col" className="govuk-table__header govuk-table__header--numeric">
                  {tt('charts.share')}
                </th>
              </tr>
            </thead>
            <tbody className="govuk-table__body">
              {chart.items.map((item, i) => {
                const slice = item.slice ?? null;
                const charted = slice !== null && slice >= 0;
                return (
                  <tr
                    key={i}
                    className={`govuk-table__row${charted && active === slice ? ' app-chart__row--active' : ''}`}
                    onMouseEnter={charted ? () => setActive(slice) : undefined}
                    onMouseLeave={charted ? () => setActive(null) : undefined}
                  >
                    <th scope="row" className="govuk-table__header">
                      {charted && <span className="app-donut__swatch" style={{ background: sliceColour(slice, chart.slices[slice]!) }} aria-hidden="true" />}
                      <span lang={item.label.lang ?? undefined}>{item.label.value}</span>
                    </th>
                    <td className="govuk-table__cell govuk-table__cell--numeric">{fmt.number(item.value)}</td>
                    <td className="govuk-table__cell govuk-table__cell--numeric">
                      {charted && total > 0 ? fmt.percent(Number(item.value) / total) : Number(item.value) === 0 ? fmt.percent(0) : tt('charts.notCharted')}
                    </td>
                  </tr>
                );
              })}
              <tr className="govuk-table__row">
                <th scope="row" className="govuk-table__header">
                  {tt('charts.total')}
                </th>
                <td className="govuk-table__cell govuk-table__cell--numeric">
                  <strong>{fmt.number(chart.total)}</strong>
                </td>
                <td className="govuk-table__cell govuk-table__cell--numeric">{total > 0 ? fmt.percent(1) : '–'}</td>
              </tr>
            </tbody>
          </table>
          {chart.notes.map((note, i) => (
            <p key={i} className="govuk-body-s app-chart__note" lang={note.lang ?? undefined}>
              {note.value}
            </p>
          ))}
        </div>
      </div>
    </section>
  );
}
